'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { parseVCards, VCard4 } from 'vcard4-ts';

interface VCardViewProps {
  url: string;
  altText: string;
}

/**
 * VCardView component fetches, parses, and displays vCard contact information
 */
const VCardView: React.FC<VCardViewProps> = ({ url, altText }) => {
  const [vcard, setVCard] = useState<VCard4 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch and parse vCard file
  useEffect(() => {
    const fetchAndParse = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch the .vcf file
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch VCF file: ${response.statusText}`);
        }

        // Get text content
        const vcfText = await response.text();

        // Parse using vcard4-ts
        const cards = parseVCards(vcfText.trim());

        // Use first vCard in file
        if (cards.vCards && cards.vCards.length > 0) {
          setVCard(cards.vCards[0]);
        } else {
          throw new Error('No valid vCard found in the file.');
        }
      } catch (e) {
        console.error('vCard parsing error:', e);
        setError('Could not read or parse the vCard file.');
      } finally {
        setLoading(false);
      }
    };

    fetchAndParse();
  }, [url]);

  // Display name from FN property
  const displayName = useMemo(() => {
    if (!vcard?.FN?.[0]) return altText || '';
    return vcard.FN[0].value;
  }, [vcard, altText]);

  // Display initials for photo placeholder
  const displayInitials = useMemo(() => {
    if (!vcard) return '';

    // Prefer using structured N property for accurate initials
    if (vcard.N && Array.isArray(vcard.N) && vcard.N.length > 0) {
      const name = vcard.N[0];
      if (typeof name === 'object' && 'value' in name) {
        const given = name.value.givenNames[0] || '';
        const family = name.value.familyNames[0] || '';
        return `${given.charAt(0)}${family.charAt(0)}`.toUpperCase();
      }
    }

    // Fallback: parse FN property
    const nameParts = displayName.split(' ');
    const firstInitial = nameParts[0] ? nameParts[0].charAt(0) : '';
    const lastInitial =
      nameParts.length > 1 ? nameParts[nameParts.length - 1].charAt(0) : '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  }, [vcard, displayName]);

  // Organization name
  const organization = useMemo(() => {
    if (!vcard?.ORG?.[0]) return '';

    // ORG value is an array of strings (company, department, etc.)
    const orgValue = vcard.ORG[0].value.join(', ');
    return orgValue.split(';').join(', ');
  }, [vcard]);

  // Photo data URL
  const photoDataUrl = useMemo(() => {
    if (!vcard?.PHOTO?.[0]) return null;

    const photo = vcard.PHOTO[0];
    // Get MIME type from parameters or default to jpeg
    const mimeType = photo.parameters?.TYPE?.[0] || 'image/jpeg';

    // Convert base64 to data URL
    return `data:image/${mimeType.toLowerCase()};base64,${photo.value}`;
  }, [vcard]);

  // Format phone number for display
  const formatPhoneNumber = (phone: string): string => {
    // Basic formatting - can be enhanced with libphonenumber-js if needed
    return phone;
  };

  // Format address for display
  const formatAddress = (adr: {
    value: {
      streetAddress?: string[];
      locality?: string[];
      region?: string[];
      postalCode?: string[];
      countryName?: string[];
    };
    parameters?: { TYPE?: string[] };
  }): React.ReactNode => {
    const parts = [];

    if (adr.value.streetAddress?.[0]) parts.push(adr.value.streetAddress[0]);

    const cityStateZip = [
      adr.value.locality?.[0],
      adr.value.region?.[0],
      adr.value.postalCode?.[0],
    ]
      .filter(Boolean)
      .join(' ');

    if (cityStateZip) parts.push(cityStateZip);
    if (adr.value.countryName?.[0]) parts.push(adr.value.countryName[0]);

    return (
      <>
        {parts.map((part, idx) => (
          <React.Fragment key={idx}>
            {part}
            {idx < parts.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    );
  };

  // Get type label from parameters
  const getTypeLabel = (parameters?: { TYPE?: string | string[] }): string | null => {
    if (!parameters?.TYPE) return null;
    return Array.isArray(parameters.TYPE) ? parameters.TYPE.join(', ') : parameters.TYPE;
  };

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-900">
        <div className="text-center">
          <span className="loading loading-spinner loading-lg text-gray-400"></span>
          <div className="mt-4 text-sm text-gray-500">Loading vCard...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-900 p-6">
        <div className="alert alert-error max-w-md">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 flex-shrink-0 stroke-current"
            fill="none"
            viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!vcard) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-900">
        <div className="text-sm text-gray-500">No vCard data available</div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col items-center overflow-y-auto bg-gray-900 p-8">
      <div className="dark:bg-base-100 w-full max-w-2xl rounded-lg bg-white p-8 shadow-sm">
        {/* Header Section */}
        <div className="mb-8 flex flex-col items-center text-center">
          {/* Photo or Initials Circle */}
          <div className="dark:bg-base-200 mb-4 flex h-32 w-32 items-center justify-center overflow-hidden rounded-full bg-gray-200">
            {photoDataUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={photoDataUrl}
                alt={displayName}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="text-5xl font-bold text-gray-600 dark:text-gray-400">
                {displayInitials}
              </div>
            )}
          </div>

          {/* Name */}
          <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-gray-100">
            {displayName}
          </h2>

          {/* Job Title */}
          {vcard.TITLE?.[0] && (
            <p className="mb-1 text-base text-gray-600 dark:text-gray-400">
              {vcard.TITLE[0].value}
            </p>
          )}

          {/* Organization */}
          {organization && (
            <p className="text-base text-gray-600 dark:text-gray-400">{organization}</p>
          )}
        </div>

        {/* Details Section */}
        <div className="space-y-6">
          {/* Phone Numbers */}
          {vcard.TEL && vcard.TEL.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-200">
                Phone
              </h3>
              {vcard.TEL.map((phone, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {formatPhoneNumber(phone.value)}
                  </span>
                  {getTypeLabel(phone.parameters) && (
                    <span className="badge badge-sm badge-outline dark:border-gray-600 dark:text-gray-400">
                      {getTypeLabel(phone.parameters)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Email Addresses */}
          {vcard.EMAIL && vcard.EMAIL.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-200">
                Email
              </h3>
              {vcard.EMAIL.map((email, index) => (
                <div key={index} className="flex items-center justify-between">
                  <a
                    href={`mailto:${email.value}`}
                    className="link link-primary text-sm dark:text-blue-400"
                    target="_blank"
                    rel="noopener noreferrer">
                    {email.value}
                  </a>
                  {getTypeLabel(email.parameters) && (
                    <span className="badge badge-sm badge-outline dark:border-gray-600 dark:text-gray-400">
                      {getTypeLabel(email.parameters)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Postal Addresses */}
          {vcard.ADR && vcard.ADR.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-200">
                Address
              </h3>
              {vcard.ADR.map((adr, index) => (
                <div key={index} className="space-y-1">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    {formatAddress(adr)}
                  </div>
                  {getTypeLabel(adr.parameters) && (
                    <span className="badge badge-sm badge-outline dark:border-gray-600 dark:text-gray-400">
                      {getTypeLabel(adr.parameters)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Websites */}
          {vcard.URL && vcard.URL.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-200">
                Website
              </h3>
              {vcard.URL.map((urlItem, index) => (
                <div key={index}>
                  <a
                    href={urlItem.value}
                    className="link link-primary text-sm dark:text-blue-400"
                    target="_blank"
                    rel="noopener noreferrer">
                    {urlItem.value}
                  </a>
                </div>
              ))}
            </div>
          )}

          {/* Notes */}
          {vcard.NOTE && vcard.NOTE.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-200">
                Note
              </h3>
              <p className="text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                {vcard.NOTE[0].value}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VCardView;
